<template>
  <div class="flex flex-col gap-2">
    <PrimeInputGroup class="flex" style="border: none">
      <PrimeInputGroupAddon class="min-w-10" style="border: none; padding: 0">
        <PrimeSelect
          v-model="selectedCountry"
          :options="countries"
          option-label="name"
          filter
          class="rounded-r-none"
          style="
            padding: 0;
            width: fit-content;
            border-top-right-radius: 0px;
            border-bottom-right-radius: 0px;
          "
          @change="emitPhoneNumber"
        >
          <template #value="slotProps">
            <div class="flex items-center gap-2">
              <span>{{ slotProps.value?.dial_code || $t('components.settings.phoneInput.select') }}</span>
            </div>
          </template>
          <template #option="slotProps">
            <div class="flex max-w-[80vw] items-center gap-2">
              <span class="w-10">{{ slotProps.option.dial_code }}</span>
              <PrimeDivider layout="vertical" />
              <span class="max-w-full text-ellipsis">
                {{ $t(`countries.${slotProps.option.url}`) }}
              </span>
            </div>
          </template>
        </PrimeSelect>
      </PrimeInputGroupAddon>
      <PrimeFloatLabel variant="on" class="flex-1">
        <PrimeInputText
          v-model="phoneNumber"
          type="tel"
          input-id="phone-input"
          :class="{ 'p-invalid': isInvalid }"
          aria-describedby="phone-help"
          maxlength="15"
          @input="emitPhoneNumber"
          @keypress="onKeyPress"
          @blur="validateNumber"
        />
        <label for="phone-input" class="text-sm">
          {{ $t('components.settings.phoneInput.phoneNumber') }}
        </label>
      </PrimeFloatLabel>
    </PrimeInputGroup>
    <small v-if="isInvalid" class="text-red-500">
      {{ $t('components.settings.phoneInput.invalid') }}
    </small>
  </div>
</template>

<script>
import { mapState } from 'pinia';
import countries from '~/assets/jsons/countryCodes.json';

export default defineNuxtComponent({
  emits: ['phone-number'],
  data() {
    return {
      phoneNumber: '',
      countries,
      selectedCountry: null,
      isInvalid: false,
    };
  },
  computed: {
    ...mapState(useAuthStore, ['profile']),
  },
  mounted() {
    // Set default country based on user's location or first country in list
    const foundCountry = this.countries.find(
      e => e.code === this.profile?.location?.country
    );
    this.selectedCountry = foundCountry || this.countries[0];

    // Handle existing phone number if available
    if (this.profile?.phone) {
      this.setPhoneFromProfile();
    }
  },
  methods: {
    setPhoneFromProfile() {
      const phoneValue = this.profile.phone;
      
      // Extract country code and number
      if (phoneValue && typeof phoneValue === 'string') {
        // Find the dial code from the phone number (format: "+90 5532308945")
        const dialCodeMatch = phoneValue.match(/^\+(\d+)\s/);
        
        if (dialCodeMatch) {
          const dialCode = `+${dialCodeMatch[1]}`;
          
          // Find the country by dial code
          const country = this.countries.find(c => c.dial_code === dialCode);
          if (country) {
            this.selectedCountry = country;
          }
          
          // Extract the phone number part (without dial code)
          this.phoneNumber = phoneValue.replace(/^\+\d+\s/, '');
        } else {
          // If format doesn't match, just set the whole value as phoneNumber
          this.phoneNumber = phoneValue;
        }
        
        // Emit the full number after setup
        this.$nextTick(() => {
          this.emitPhoneNumber();
        });
      }
    },
    validateNumber() {
      const digits = this.phoneNumber.replace(/\D/g, '');
      if (digits.length > 0) {
        this.isInvalid = digits.length < 10 || digits.length > 15;
      }
    },
    onKeyPress(event) {
      const charCode = event.which ? event.which : event.keyCode;
      if (charCode > 31 && (charCode < 48 || charCode > 57)) {
        event.preventDefault();
      }
    },
    emitPhoneNumber() {
      const cleanNumber = this.phoneNumber.replace(/\s+/g, '');
      const fullPhoneNumber = `${this.selectedCountry.dial_code} ${cleanNumber}`;
      this.$emit('phone-number', fullPhoneNumber);
    },
  },
});
</script>

<style scoped>
:deep(.p-inputtext.p-invalid) {
  border-color: #dc2626;
}
</style>